import {
  CircleNotchIcon,
  HeadphonesIcon,
  type Icon,
  PlayCircleIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { lazy, Suspense, useEffect, useId, useRef, useState } from "react";
import { useQuery } from "urql";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";
import {
  GetBroadcastClipPlayerUrl,
  GetPodcastEpisodePlayerUrl,
} from "@/data-access/discovery-view/queries/discovery-view-queries";

const TVEyesPlayerModal = lazy(
  () => import("@/components/discovery/modals/TVEyesPlayerModal"),
);

export interface TVEyesPlayerMeta {
  title?: string | null;
  subtitle?: string | null;
}

interface TVEyesPlayerProps {
  id: string;
  feedId: string;
  type: "broadcast" | "podcast";
  variant?: "play" | "headphones";
  triggerLabel?: string;
  meta?: TVEyesPlayerMeta;
}

interface TVEyesPlayerUrlResult {
  playerUrl?: string | null;
}

interface TVEyesPlayerConnection {
  results?: TVEyesPlayerUrlResult[] | null;
}

interface TVEyesPlayerQueryData {
  broadcastClips?: TVEyesPlayerConnection | null;
  podcastEpisodes?: TVEyesPlayerConnection | null;
}

export const resolveTVEyesPlayerUrl = (
  type: TVEyesPlayerProps["type"],
  data: TVEyesPlayerQueryData | undefined,
) => {
  const results =
    type === "broadcast"
      ? data?.broadcastClips?.results
      : data?.podcastEpisodes?.results;

  return results?.[0]?.playerUrl ?? null;
};

/** `true` when `url` points to `archive.tveyes.com` (saved clip) vs `mediaviewport.tveyes.com` (editor). */
export const isSavedMediaUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  try {
    return new URL(url).host === "archive.tveyes.com";
  } catch {
    return false;
  }
};

const TVEyesPlayer = ({
  id,
  feedId,
  type,
  variant = "play",
  triggerLabel,
  meta,
}: TVEyesPlayerProps) => {
  const titleId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  // Tracks the fetching value from the previous render to detect 1→0 transitions.
  const prevFetchingRef = useRef(false);

  const [
    { data: broadcastData, fetching: broadcastFetching, error: broadcastError },
    reexecuteBroadcast,
  ] = useQuery({
    query: GetBroadcastClipPlayerUrl,
    variables: { id, feedId },
    pause: !isOpen || type !== "broadcast",
    requestPolicy: "network-only",
  });

  const [
    { data: podcastData, fetching: podcastFetching, error: podcastError },
    reexecutePodcast,
  ] = useQuery({
    query: GetPodcastEpisodePlayerUrl,
    variables: { id, feedId },
    pause: !isOpen || type !== "podcast",
    requestPolicy: "network-only",
  });

  const fetching = type === "broadcast" ? broadcastFetching : podcastFetching;
  const error = type === "broadcast" ? broadcastError : podcastError;

  const playerUrl =
    type === "broadcast"
      ? resolveTVEyesPlayerUrl(type, broadcastData)
      : resolveTVEyesPlayerUrl(type, podcastData);

  const handleClose = () => {
    setIsOpen(false);
    setReloading(false);
  };

  // Close on missing URL / error so the next click retriggers the query.
  useEffect(() => {
    if (isOpen && !fetching && (error || !playerUrl)) {
      handleClose();
    }
  }, [isOpen, fetching, error, playerUrl]);

  // Clear reloading when URL leaves archive, or on fetching 1→0 (handles same-archive refetch).
  useEffect(() => {
    const wasFetching = prevFetchingRef.current;
    prevFetchingRef.current = fetching;
    if (!reloading) return;
    if (!isSavedMediaUrl(playerUrl) || (wasFetching && !fetching)) {
      setReloading(false);
    }
  }, [reloading, fetching, playerUrl]);

  // Re-fetch the player URL after unsave so the iframe reloads into the editor.
  const handleUnsaved = () => {
    setReloading(true);
    if (type === "broadcast") {
      reexecuteBroadcast();
    } else {
      reexecutePodcast();
    }
  };

  const PlayerIcon: Icon =
    variant === "headphones" ? HeadphonesIcon : PlayCircleIcon;

  const label =
    triggerLabel ??
    (variant === "headphones"
      ? `Listen to: ${meta?.title ?? "podcast episode"}`
      : `View clip: ${meta?.title ?? "broadcast clip"}`);

  const isFetching = isOpen && fetching;

  const triggerIcon = error ? (
    <WarningCircleIcon
      size={20}
      className="text-red-500"
      aria-label="Failed to load media player — click to retry"
    />
  ) : isFetching ? (
    <CircleNotchIcon className="animate-spin" size={20} aria-hidden />
  ) : (
    <PlayerIcon size={20} aria-hidden />
  );

  return (
    <>
      <Button
        variant="ghost"
        size="none"
        onPress={() => setIsOpen(true)}
        isDisabled={isFetching}
        aria-label={
          error ? "Failed to load media player — click to retry" : label
        }
        className="text-blue-600 hover:text-blue-800 flex items-center justify-center size-5"
      >
        {triggerIcon}
      </Button>

      {isOpen && typeof playerUrl === "string" && (!fetching || reloading) && (
        <Suspense fallback={null}>
          <TVEyesPlayerModal
            titleId={titleId}
            playerUrl={playerUrl}
            meta={meta}
            onClose={handleClose}
            reloading={reloading}
            unsave={
              isSavedMediaUrl(playerUrl)
                ? {
                    type,
                    id,
                    feedId,
                    onUnsaved: handleUnsaved,
                    onError: toast.error,
                  }
                : undefined
            }
          />
        </Suspense>
      )}
    </>
  );
};

export default TVEyesPlayer;
